/**
 * lazyWithRetry — wraps React.lazy with a one-time auto-reload on
 * dynamic-import failure.
 *
 * Why: in production we ship hashed chunks. After a deploy, the user's
 * cached index.html still references OLD chunk hashes. When they navigate
 * to a lazy-loaded page, the import fails (404 or CONNECTION_REFUSED if
 * the server is restarting). Default behavior: app crashes into the
 * PageErrorBoundary; user has to manually refresh.
 *
 * What this does: on the first chunk-load failure within a session, force
 * a single hard reload — the browser re-fetches index.html (which is
 * served `cache-control: no-cache`) and gets fresh chunk references. The
 * sessionStorage gate prevents an infinite reload loop if the failure is
 * a genuine bug, not a stale-bundle issue.
 *
 * 2026-04-28: added after the post-reboot Dashboard / PnL crash where
 * the browser tab kept references to chunks that briefly returned
 * CONNECTION_REFUSED while nginx was restarting.
 */
import { lazy, type ComponentType } from "react";

const RELOAD_FLAG = "lazy_chunk_reload_attempted";

// Match React.lazy's signature exactly so wrapped lazy components keep
// their original prop types (T extends ComponentType<any>, not <unknown>).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await importFn();
      // Successful load — clear the flag so future failures can also retry.
      try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* private mode etc */ }
      return mod;
    } catch (err) {
      // Likely "Failed to fetch dynamically imported module" — usually
      // stale chunk references after a deploy or transient server-down.
      const alreadyRetried = (() => {
        try { return sessionStorage.getItem(RELOAD_FLAG) === "1"; } catch { return false; }
      })();
      if (!alreadyRetried) {
        try { sessionStorage.setItem(RELOAD_FLAG, "1"); } catch { /* ignore */ }
        // Hard reload — bypasses bfcache, re-fetches index.html.
        window.location.reload();
        // Return an unresolving promise so React Suspense waits for the reload
        // to actually navigate away, instead of momentarily flashing an error.
        return new Promise<{ default: T }>(() => {});
      }
      // Already retried once this session — propagate so PageErrorBoundary
      // can show the operator a real error message.
      throw err;
    }
  });
}
